// noinspection ES6UnusedImports

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { insertContentToWriter, requireViewRange } from '../utils';
import { createModelAttributeName, findViewChild, setupVjoonAttributeConversion } from './utils';
import DowncastWriter from '@ckeditor/ckeditor5-engine/src/view/downcastwriter';
import UpcastWriter from '@ckeditor/ckeditor5-engine/src/view/upcastwriter';
import Config from '@ckeditor/ckeditor5-utils/src/config';
import ModelElement from '@ckeditor/ckeditor5-engine/src/model/element';
import Mapper from '@ckeditor/ckeditor5-engine/src/conversion/mapper';
import Image from '@ckeditor/ckeditor5-image/src/image.js';

export default class VjoonImage extends Plugin {

	/**
	 * config.createUrl( {
	 *			id: 'object-id',
	 *	    	src: 'http://your.image',
	 *	        type: 'custom' (optional)
	 *	        version: 2 (optional)
	 *	    } );
	 *
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );

		/**
		 * The configuration of this plugins.
		 *
		 * @private
		 * @type {Config}
		 */
		this._config = editor.config.get( 'vjoonImage' ) || {};
	}

	static get pluginName() {
		return 'vjoonImage';
	}

	static get requires() {
		return [ Image ];
	}

	init() {
		const editor = this.editor;
	}

	/**
	 * @inheritDoc
	 */
	afterInit() {
		const editor = this.editor;
		['imageInline', 'imageBlock'].forEach(modelElement => {
			setupVjoonAttributeConversion( 'img', modelElement, 'id', editor );
			// setupVjoonAttributeConversion( 'img', modelElement, 'publicationid', editor );
			setupVjoonAttributeConversion( 'img', modelElement, 'version', editor );
			setupVjoonAttributeConversion( 'img', modelElement, 'type', editor, false );

			// editor.model.schema.extend( modelElement, { allowAttributes: [ createModelAttributeName( 'src' ) ] } );
			editor.conversion.for( 'editingDowncast' ).add( updateImageUrlForEditor( modelElement, this._config ) );
		})


		this.imageUtils = this.editor.plugins.get( 'ImageUtils' );
	}

	/**
	 * Insert image
	 *		insertImages( {
	 *			id: 'object-id',
	 *	    	src: 'http://your.image',
	 *	        alt: 'Alternate Text',
	 *	        caption: 'Image Caption',
	 *	        type: 'custom' (optional),
	 *	        version: 2 (optional),
	 *	        imageType: 'imageBlock' (optional)
	 *	    }, event );
	 *
	 * It is also possible to insert multiple images at once:
	 *
	 *		insertImages(
	 *			[
	 *              {
	 *					id: 'image-1',
	 *	    	        src: 'http://your.image',
	 *	                alt: 'Alternate Text',
	 *	                caption: 'Image Caption',
	 *	        		type: 'logo'
	 *	                version: 1
	 *	            },
	 *              {
	 *					id: 'image-2',
	 *	    	        src: 'http://your.second.image',
	 *	                alt: 'Alternate Text',
	 *	                caption: 'Image Caption',
	 *	                imageType: 'imageInline'
	 *	            }
	 *			]
	 *		}, event, true );
	 */
	insertImages( images, event, useOptimalPosition = true  ) {
		event.stopPropagation();
		event.preventDefault();
		const viewRange = requireViewRange( this.editor, this.editor.editing.view, event );
		this.editor.model.change( writer => {
			const imagesToCreate = Array.isArray( images ) ? images : [ images ];

			for ( const image of imagesToCreate ) {
				const imageElement = this._createImageModel(writer, image);
				insertContentToWriter(this.editor, writer, viewRange, imageElement, useOptimalPosition);
			}
		} );
	}

	/**
	 * Update image version
	 *		updateImages( {
	 *			id: 'object-id',
	 *	        version: 2 (optional)
	 *	    } );
	 *
	 * It is also possible to insert multiple images at once:
	 *
	 *		updateImages(
	 *			[
	 *              {
	 *					id: 'image-1',
	 *	    	        src: 'http://your.image',
	 *	                version: 2
	 *	            },
	 *              {
	 *					id: 'image-2',
	 *	                version: 3
	 *	            }
	 *			]
	 *		} );
	 */
	updateImages( images ) {
		// enqueueChange
		this.editor.model.change( writer => {
			const imageModelsToUpdate = this._findImageModelsById( images );
			imageModelsToUpdate.forEach( ( image, modelItem ) => writer.setAttributes( {
				'vjoon-version': image.version
			}, modelItem ) );
		} );
	}

	/**
	 * Refresh  image version
	 *		refreshImages( {
	 *			id: 'object-id',
	 *	    } );
	 *
	 * It is also possible to insert multiple images at once:
	 *
	 *		refreshImages(
	 *			[
	 *              {
	 *					id: 'image-1'
	 *	            },
	 *              {
	 *					id: 'image-2'
	 *	            }
	 *			]
	 *		} );
	 */
	refreshImages( images ) {
		const imageModelsToRefresh = this._findImageModelsById( images );
		this._refreshImageModels( imageModelsToRefresh );
	}

	refreshAllImages() {
		const imageModelsToRefresh = this._findImageModels( ( imageId ) => ( {
			id: `${ imageId }`
		} ), ( imageId ) => !!imageId );
		this._refreshImageModels( imageModelsToRefresh );
	}

	_refreshImageModels( imageModelsToRefresh ) {
		this.editor.editing.view.change( writer => {
			const mapper = this.editor.editing.mapper;
			imageModelsToRefresh.forEach( ( image, modelElement ) =>
				_updateImageUrl( this._config.createUrl, modelElement, mapper, writer ) );
		} );
	}

	_findImageModelsById( images ) {
		const imagesById = this._createImagesById( images );
		const imageIds = new Set( imagesById.keys() );
		return this._findImageModels( ( imageId ) => imagesById.get( imageId ), ( imageId ) => imageIds.has( imageId ) );
	}

	_createImagesById( images ) {
		const imagesArray = Array.isArray( images ) ? images : [ images ];
		return new Map( imagesArray.map( image => [ image.id, image ] ) );
	}

	/**
	 * Find all images which are return true for an imageId.
	 * @param {Function} imageConfigProvider
	 * @param {Function} predicate
	 * @private
	 */
	_findImageModels( imageConfigProvider, predicate ) {
		const modelsWithId = new Map();
		for (const modelNode of this.editor.model.document.getRoot().getChildren()) {
			const imageId = modelNode.getAttribute( 'vjoon-id' );
			if (this.imageUtils.isImage( modelNode ) && !!imageId && predicate( imageId )) {
				modelsWithId.set( modelNode, imageConfigProvider( imageId ) );
			}
		}
		return modelsWithId;
	}

	_createImageModel( writer, image ) {
		const imageType = !!image?.imageType ? image?.imageType : 'imageBlock';
		const imageElement = writer.createElement( imageType, {
			src: image.src,
			alt: image.alt,
			'vjoon-id': image.id,
			'vjoon-version': image.version,
			'vjoon-type': image.type
		} );
		if (!this.imageUtils.isInlineImage(imageElement) && image.caption) {
			const caption = writer.createElement( 'caption' );
			writer.append( caption, imageElement );
			// writer.appendElement( 'caption', imageElement );
			writer.appendText( image.caption, caption );
		}
		return imageElement;
	}
}

/**
 * Updates the src attribute of an image.
 * @param {String} modelElementName: 'imageBlock' | 'imageInline'
 * @param {Config} config
 * @param {String} attributeName
 * @return {Function} {DowncastDispatcher} dispatcher
 */
function updateImageUrlForEditor( modelElementName, config, attributeName = 'vjoon-version' ) {
	return dispatcher => dispatcher.on( `attribute:${ attributeName }:${ modelElementName }`, ( evt, data, conversionApi ) => {
		// return dispatcher => dispatcher.on( 'insert:image', ( evt, data, conversionApi ) => {
		const modelElement = data.item;
		if (!conversionApi.consumable.consume( modelElement, evt.name )) {
			return;
		}
		_updateImageUrl( config.createUrl, modelElement, conversionApi.mapper, conversionApi.writer );
	}, { priority: 'low' } );
}

/**
 * Updates the src attribute of an image.
 * @param {Function} createUrl
 * @param {ModelElement} modelElement
 * @param {Mapper} mapper
 * @param {DowncastWriter, UpcastWriter} writer
 */
function _updateImageUrl( createUrl, modelElement, mapper, writer ) {
	if (!createUrl) {
		return;
	}
	const viewFigure = mapper.toViewElement( modelElement );
	const viewElement = findViewChild( viewFigure, 'img', writer );

	if (!viewElement) {
		return;
	}

	const image = {
		id: getModelAttributeName( modelElement, 'id' ),
		src: getModelAttribute( modelElement, 'src' ),
		type: getModelAttributeName( modelElement, 'type' ),
		version: getModelAttributeName( modelElement, 'version', 0 )
	};
	const url = createUrl( image ) || modelElement.getAttribute( 'src' );
	if (url) {
		writer.setAttribute( 'src', url, viewElement );
	}

	function getModelAttributeName( modelElement, modelAttributeName, defaultValue = '' ) {
		return getModelAttribute( modelElement, createModelAttributeName( modelAttributeName ), defaultValue );
	}

	function getModelAttribute( modelElement, modelAttribute, defaultValue = '' ) {
		return modelElement.getAttribute( modelAttribute ) || defaultValue;
	}
}
